import React, { useEffect, useState } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { db } from "../../firebase";
import { formatCurrencyUY } from "../../utils/formatters";
import { collection, query, where, getDocs } from "firebase/firestore";
import { tolerantId } from "../../utils/firestoreHelpers";
import {
  FaMoneyBillWave,
  FaExclamationTriangle,
  FaChartLine,
  FaBuilding,
  FaSync,
  FaPercentage,
  FaCreditCard,
  FaNewspaper,
  FaReceipt,
  FaComments,
  FaBolt,
} from "react-icons/fa";
import { colors, radius, spacing, typography, transitions, cardBase, btnSecondary, pageContainer } from "../../utils/designTokens";
import { HABI_API_BASE_URL, getAuthHeaders } from "../../utils/habiApi";
import KPICard from "../../components/KPICard";
import KpiInfoTooltip from "../../components/KpiInfoTooltip";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import AdminModal from "../../components/AdminModal";
import PillButton from "../../components/PillButton";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import { getKpiBadgeColor } from "../../utils/kpiDefinitions";
import { calculateDelinquencyByUnits, calculateDelinquencyByAmount, calculateOperatingRatio, calculateReserveMonths } from "../../utils/kpiCalculations";
import { computeCurrentFundBalances } from "../../utils/fundMovements";
import { esPagoDeAlquiler } from "../../utils/unidadesEdificio";
import { useAdminBadges } from "../../hooks/useAdminBadges";
import { asambleaAdmin, abrirModoSala, horaMontevideo, modoSalaDisponible, ventanaModoSala } from "../../utils/asambleas/api";
import { useAhoraEnLimites } from "../../utils/asambleas/reloj";

const HomeAdmin = ({ setSelected }) => {
  const { buildings, selectedBuilding, setSelectedBuilding } = useBuilding();
  // Pendientes en tiempo real (misma fuente que los badges del sidebar).
  const adminBadges = useAdminBadges(selectedBuilding?.buildingid);

  // Helper: navega a una pantalla pero antes filtra al edificio del item
  // (sino el destino abre con "Todos los edificios" y el admin pierde contexto).
  const goToBuildingScreen = (alertItem, screenId) => {
    if (alertItem?.id) {
      const target = buildings.find((b) => b.id === alertItem.id);
      if (target) setSelectedBuilding(target);
    }
    setSelected(screenId);
  };
  const [loading, setLoading] = useState(true);
  // Asambleas convocadas de los próximos 14 días del edificio seleccionado (y
  // las de hoy que empezaron hace hasta 10 h: el Modo Sala sigue abierto).
  const [asambleasProximas, setAsambleasProximas] = useState([]);
  useEffect(() => {
    let vivo = true;
    const bid = selectedBuilding?.buildingid;
    if (!bid) {
      setAsambleasProximas([]);
      return undefined;
    }
    asambleaAdmin("listar", { buildingId: bid })
      .then((res) => {
        if (!vivo) return;
        const ahora = Date.now();
        const hasta = ahora + 14 * 24 * 3600 * 1000;
        setAsambleasProximas(
          (res.asambleas || [])
            .filter((a) => (a.estado === "convocada" || a.estado === "en_curso") &&
              Number(a.inicioMs) >= ahora - 10 * 3600 * 1000 && Number(a.inicioMs) <= hasta)
            .sort((x, y) => Number(x.inicioMs) - Number(y.inicioMs))
        );
      })
      // Módulo apagado o sin desplegar: el inicio no muestra el bloque.
      .catch(() => vivo && setAsambleasProximas([]));
    return () => {
      vivo = false;
    };
  }, [selectedBuilding]);
  // "Asambleas de hoy" (presenciales o mixtas, con el Modo Sala): la hora se
  // renueva sola en los bordes de la ventana (T-3 h habilita el botón).
  const ahoraAsambleas = useAhoraEnLimites(
    asambleasProximas.flatMap((a) => {
      const v = ventanaModoSala(a);
      return v ? [v.desdeMs, v.hastaMs] : [];
    })
  );
  const asambleasHoy = asambleasProximas.filter((a) => modoSalaDisponible(a, ahoraAsambleas).mostrar);
  const asambleasSiguientes = asambleasProximas.filter(
    (a) => !asambleasHoy.includes(a) && Number(a.inicioMs) >= ahoraAsambleas - 6 * 3600 * 1000
  );
  const [metrics, setMetrics] = useState({
    recaudacionTotal: 0,
    recaudacionMeta: 0,
    pctCumplimiento: 0,
    tasaMorosidad: 0,
    tendenciaMorosidad: 0,
    incidenciasCriticas: 0,
    incidenciasTotal: 0,
    flujoCaja: 0,
    ingresos: 0,
    egresos: 0,
    // KPIs nuevos
    morosidadUnidades: { rate: 0, unitsOverdue: 0, totalUnits: 0 },
    morosidadMonto: { rate: 0, overdueAmount: 0, totalAmount: 0 },
    ratioGastos: { ratio: 0 },
    mesesReserva: { months: 0 },
  });
  const [buildingsHealth, setBuildingsHealth] = useState([]);

  // A3 — Alertas IA (anomalías de gastos)
  const [aiAlerts, setAiAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  // D6 — Briefing diario del staff
  const [dailyBriefing, setDailyBriefing] = useState(null);
  const [loadingBriefing, setLoadingBriefing] = useState(false);
  const [showBriefing, setShowBriefing] = useState(false);

  // F3 — Benchmark entre edificios
  const [showBenchmark, setShowBenchmark] = useState(false);
  const [benchmark, setBenchmark] = useState(null);
  const [loadingBenchmark, setLoadingBenchmark] = useState(false);

  // D4 — Sentiment del edificio
  const [sentiment, setSentiment] = useState(null);
  const [showSentiment, setShowSentiment] = useState(false);

  // A3 — modal de alertas IA
  const [showAlerts, setShowAlerts] = useState(false);

  const fetchSentiment = async () => {
    if (!selectedBuilding) {
      setSentiment(null);
      return;
    }
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${HABI_API_BASE_URL}/api/sentiment/${selectedBuilding.buildingid}`,
        { headers }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      setSentiment(data);
    } catch (e) {
      console.warn("[D4] sentiment fetch failed:", e);
    }
  };

  useEffect(() => {
    fetchSentiment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding]);

  const fetchBenchmark = async () => {
    if (!selectedBuilding) return;
    try {
      setLoadingBenchmark(true);
      setBenchmark(null);
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${HABI_API_BASE_URL}/api/benchmark/${selectedBuilding.buildingid}?months_window=3`,
        { headers }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      setBenchmark(data);
    } catch (e) {
      console.warn("[F3] benchmark failed:", e);
    } finally {
      setLoadingBenchmark(false);
    }
  };

  const openBenchmark = () => {
    setShowBenchmark(true);
    if (!benchmark) fetchBenchmark();
  };

  const fetchDailyBriefing = async () => {
    try {
      const { auth } = await import("../../firebase");
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      setLoadingBriefing(true);
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${HABI_API_BASE_URL}/api/daily-briefing/${uid}`,
        { headers }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      setDailyBriefing(data);
    } catch (e) {
      console.warn("[D6] No se pudo cargar briefing:", e);
    } finally {
      setLoadingBriefing(false);
    }
  };

  const regenerateBriefing = async () => {
    try {
      setLoadingBriefing(true);
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/generate-briefing`, {
        method: "POST",
        headers,
        body: "{}",
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setDailyBriefing(data);
    } catch (e) {
      console.warn("[D6] No se pudo regenerar briefing:", e);
    } finally {
      setLoadingBriefing(false);
    }
  };

  useEffect(() => {
    fetchDailyBriefing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAIAlerts = async () => {
    if (!selectedBuilding) {
      setAiAlerts([]);
      return;
    }
    try {
      setLoadingAlerts(true);
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${HABI_API_BASE_URL}/api/ai-alerts/${selectedBuilding.buildingid}?status_filter=new&limit=20`,
        { headers }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      // Dedupe por expense_id: el backend puede haber persistido la misma
      // anomalia varias veces (cron + manual + smoke tests). Nos quedamos con
      // la mas reciente. Las alertas sin expense_id (p.ej. sentiment_drop) se
      // dedupean por id (no se pueden agrupar de otra forma).
      const items = data?.items || [];
      const byKey = new Map();
      for (const a of items) {
        const key = a.expense_id != null ? `exp:${a.expense_id}` : `id:${a.id}`;
        const existing = byKey.get(key);
        const aTs = String(a.created_at || "");
        const eTs = existing ? String(existing.created_at || "") : "";
        if (!existing || aTs > eTs) byKey.set(key, a);
      }
      const deduped = Array.from(byKey.values()).sort(
        (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))
      );
      setAiAlerts(deduped);
    } catch (e) {
      console.warn("[A3] No se pudieron cargar alertas IA:", e);
    } finally {
      setLoadingAlerts(false);
    }
  };

  const updateAlertStatus = async (alertId, newStatus, note) => {
    try {
      const headers = await getAuthHeaders();
      await fetch(`${HABI_API_BASE_URL}/api/ai-alerts/${alertId}/status`, {
        method: "POST",
        headers,
        body: JSON.stringify({ status: newStatus, note: note || null }),
      });
      // Optimistic update — quitar de la lista visible.
      setAiAlerts((prev) => prev.filter((a) => a.id !== alertId));
    } catch (e) {
      console.warn("[A3] No se pudo actualizar estado de alerta:", e);
    }
  };

  useEffect(() => {
    fetchAIAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding]);

  useEffect(() => {
    if (buildings && buildings.length > 0) {
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [buildings, selectedBuilding]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();

      let totalRecaudado = 0;
      let totalEsperado = 0;
      let totalPendiente = 0;
      let totalIncidenciasCriticas = 0;
      let totalIncidencias = 0;
      let totalIngresos = 0;
      let totalEgresos = 0;
      let totalFlujoCaja = 0;
      let totalReserva = 0;
      const buildingsData = [];

      // Acumular para KPIs mejorados
      let allPayments = [];
      let allApartments = [];

      // Usar edificios filtrados y deduplicados
      const buildingsRaw = selectedBuilding
        ? buildings.filter(b => b.buildingid === selectedBuilding.buildingid)
        : buildings;
      const seenIds = new Set();
      const buildingsToProcess = buildingsRaw.filter(b => {
        const key = String(b.buildingid);
        if (seenIds.has(key)) return false;
        seenIds.add(key);
        return true;
      });

      // Procesar cada edificio en paralelo. Antes esto era un for secuencial
      // con 6 awaits anidados por edificio → con 11 edificios eran ~66
      // round-trips Firestore en serie (7-13s de espera). Ahora cada edificio
      // dispara sus 6 queries en paralelo y todos los edificios corren a la
      // vez: el wall-time queda dominado por el query mas lento (~1s).
      const paidStatuses = ["pagado", "pagada", "aprobado", "aprobada"];

      const processBuilding = async (building) => {
        const buildingId = Number(building.buildingid);
        const buildingName = building.nombre || building.name || `Edificio ${buildingId}`;

        const [unitsSnap, paymentsSnap, expensesSnap, multasSnap, incidentsSnap, monthlySnap, incomesSnap] =
          await Promise.all([
            getDocs(query(collection(db, "apartments"), where("building", "in", tolerantId(buildingId)))),
            getDocs(query(collection(db, "payments"), where("buildingid", "==", buildingId))),
            getDocs(query(collection(db, "expenses"), where("buildingid", "==", buildingId))),
            getDocs(query(collection(db, "multas"), where("buildingid", "==", buildingId))),
            getDocs(query(collection(db, "incidents"), where("buildingid", "==", buildingId))),
            getDocs(query(
              collection(db, "montlyaccount"),
              where("buildingid", "==", buildingId),
              where("year", "==", currentYear),
              where("month", "==", currentMonth),
            )),
            // Ingresos del edificio (cartel, antena, alquileres manuales): suman al
            // saldo de los fondos. Si no se pueden leer, el saldo sale sin ellos.
            getDocs(query(collection(db, "buildingincomes"), where("buildingid", "in", tolerantId(buildingId))))
              .catch((err) => {
                console.warn("[HomeAdmin] no se pudieron leer los ingresos del edificio:", err?.code || err);
                return { docs: [] };
              }),
          ]);

        const totalUnidades = unitsSnap.size;
        const aptDocs = unitsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const payDocs = paymentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const expensesDocs = expensesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const multasDocs = multasSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const incomeDocs = incomesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // ── Recaudación: período que se está COBRANDO ──
        // Antes se miraban los pagos etiquetados con el mes calendario actual: en
        // octubre se cobra la expensa de SEPTIEMBRE (la de octubre aún no existe),
        // así que daba $0 / 0%. Además entraban los registros de pago de gastos del
        // edificio (expensesid), sin estado de cobro → contaban como "pendientes".
        // Ahora: solo cobros de residentes publicados, del último período liquidado
        // (≤ mes actual): facturado vs cobrado de ese período.
        const residentPays = payDocs.filter(
          (p) => p.userid && p.month != null && p.publishedToResidents !== false
        );
        // El alquiler de las unidades del edificio es un ingreso, no cobranza de
        // gastos comunes: no entra en recaudado/pendiente.
        const pagosGc = residentPays.filter((p) => !esPagoDeAlquiler(p));
        const curKey = currentYear * 100 + currentMonth;
        const periodKey = pagosGc.reduce((mx, p) => {
          const k = Number(p.year) * 100 + Number(p.month);
          return k <= curKey && k > mx ? k : mx;
        }, 0);
        let buildingRecaudado = 0;
        let buildingPendiente = 0;
        pagosGc.forEach((p) => {
          if (Number(p.year) * 100 + Number(p.month) !== periodKey) return;
          const amount = Number(p.amount) || 0;
          const st = (p.status || "").toLowerCase();
          const isPaid = paidStatuses.includes(st) || p.paid === true;
          const remaining = p.remainingAmount != null ? Number(p.remainingAmount) || 0 : amount;
          const cobrado = isPaid ? (Number(p.paidAmount) || amount) : Math.max(0, amount - remaining);
          buildingRecaudado += Math.min(cobrado, amount);
          buildingPendiente += Math.max(0, amount - Math.min(cobrado, amount));
        });

        const expensePayments = payDocs.filter(p => p.expensesid != null);
        const residentPayments = payDocs.filter(p => p.userid && p.month);

        const balances = computeCurrentFundBalances({
          expenses: expensesDocs,
          expensePayments,
          residentPayments,
          multas: multasDocs,
          incomes: incomeDocs,
          building,
          currency: "peso",
        });

        let buildingEgresos = 0;
        expensePayments.forEach(ep => {
          const rawDate = ep.paymentDate || ep.createdAt;
          const d = rawDate?.toDate ? rawDate.toDate() : (rawDate?.seconds ? new Date(rawDate.seconds * 1000) : new Date(rawDate));
          if (d && d.getMonth() + 1 === currentMonth && d.getFullYear() === currentYear) {
            buildingEgresos += Number(ep.amount) || 0;
          }
        });

        let buildingIncidenciasCriticas = 0;
        const buildingIncidenciasTotal = incidentsSnap.size;
        incidentsSnap.docs.forEach(doc => {
          const incident = doc.data();
          if (incident.status !== "Resuelto" && incident.status !== "resuelto") {
            buildingIncidenciasCriticas++;
          }
        });

        const cierreMesCompleto = monthlySnap.size > 0;

        // Morosidad del semáforo = MISMA fórmula que el KPI "Morosidad (Unidades)"
        // y la pantalla de Cobranza (unidades con deuda vencida / total).
        const morosidadEdificio = calculateDelinquencyByUnits(
          residentPays, aptDocs, building.paymentDueDay || 10, building
        ).rate;

        let estado = "green";
        if (morosidadEdificio > 20 || buildingIncidenciasCriticas > 5) {
          estado = "red";
        } else if (morosidadEdificio > 10 || buildingIncidenciasCriticas > 2) {
          estado = "yellow";
        }

        return {
          aptDocs,
          payDocs,
          buildingRecaudado,
          buildingPendiente,
          buildingEgresos,
          buildingIncidenciasCriticas,
          buildingsDataEntry: {
            id: building.id || `building-${buildingId}`,
            name: buildingName,
            unidades: totalUnidades,
            recaudado: buildingRecaudado,
            pendiente: buildingPendiente,
            morosidad: morosidadEdificio,
            incidenciasPendientes: buildingIncidenciasCriticas,
            incidenciasTotal: buildingIncidenciasTotal,
            cierreMes: cierreMesCompleto,
            estado,
            egresos: buildingEgresos,
          },
          buildingIncidenciasTotal,
          periodKey,
          balancesCaja: balances.caja || 0,
          balancesReserva: balances.reserva || 0,
        };
      };

      const results = await Promise.all(buildingsToProcess.map(processBuilding));

      const periodKeys = new Set();
      for (const r of results) {
        if (r.periodKey) periodKeys.add(r.periodKey);
        allApartments = allApartments.concat(r.aptDocs);
        allPayments = allPayments.concat(r.payDocs);
        buildingsData.push(r.buildingsDataEntry);
        totalRecaudado += r.buildingRecaudado;
        totalPendiente += r.buildingPendiente;
        totalIncidenciasCriticas += r.buildingIncidenciasCriticas;
        totalIncidencias += r.buildingIncidenciasTotal;
        totalIngresos += r.buildingRecaudado;
        totalEgresos += r.buildingEgresos;
        totalFlujoCaja += r.balancesCaja;
        totalReserva += r.balancesReserva;
      }

      totalEsperado = totalRecaudado + totalPendiente;
      const pctCumplimiento = totalEsperado > 0 ? (totalRecaudado / totalEsperado) * 100 : 0;
      const tasaMorosidad = totalEsperado > 0 ? (totalPendiente / totalEsperado) * 100 : 0;

      // Usar paymentDueDay del primer edificio como default (o 10). Pasamos el
      // building para respetar el período de gracia (quien paga en gracia no es moroso).
      const defaultDueDay = buildingsToProcess[0]?.paymentDueDay || 10;
      const graceBuilding = buildingsToProcess.length === 1 ? buildingsToProcess[0] : null;

      const morosidadUnidades = calculateDelinquencyByUnits(allPayments, allApartments, defaultDueDay, graceBuilding);
      const morosidadMonto = calculateDelinquencyByAmount(allPayments, defaultDueDay, graceBuilding);
      const ratioGastos = calculateOperatingRatio(totalEgresos, totalIngresos);

      // Meses de reserva: saldo de reserva / gastos comunes promedio mensuales del edificio
      const avgMonthlyExpenses = totalEgresos > 0 ? totalEgresos : 1;
      const mesesReserva = calculateReserveMonths(totalReserva, avgMonthlyExpenses);

      setMetrics({
        recaudacionTotal: totalRecaudado,
        recaudacionMeta: totalEsperado,
        // Período que se está cobrando (si todos los edificios coinciden).
        recaudacionLabel: periodKeys.size === 1
          ? (() => {
              const k = [...periodKeys][0];
              const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
              return `Recaudación ${MESES[(k % 100) - 1]} ${Math.floor(k / 100)}`;
            })()
          : "Recaudación del período",
        pctCumplimiento: pctCumplimiento,
        tasaMorosidad: tasaMorosidad,
        tendenciaMorosidad: 0,
        incidenciasCriticas: totalIncidenciasCriticas,
        incidenciasTotal: totalIncidencias,
        flujoCaja: totalFlujoCaja,
        ingresos: totalIngresos,
        egresos: totalEgresos,
        morosidadUnidades,
        morosidadMonto,
        ratioGastos,
        mesesReserva,
      });

      const sorted = buildingsData.sort((a, b) => {
        const order = { red: 0, yellow: 1, green: 2 };
        return order[a.estado] - order[b.estado];
      });
      setBuildingsHealth(sorted);

    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = formatCurrencyUY;

  // Mapeo de estado a colores del design system
  // Determinar color del badge de cumplimiento
  const getCumplimientoBadgeColor = (pct) => {
    if (pct >= 80) return "success";
    if (pct >= 50) return "warning";
    return "error";
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <LoadingState variant="block" message="Cargando dashboard…" />
      </div>
    );
  }

  if (!buildings || buildings.length === 0) {
    return (
      <div style={styles.container}>
        <EmptyState
          icon={FaBuilding}
          title="Sin edificios asignados"
          description="No tenés edificios asignados para administrar. Pedile al superadmin que te asigne edificios."
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Dashboard</h1>
          <p style={styles.subtitle}>
            {selectedBuilding
              ? `Filtrado: ${selectedBuilding.name || `Edificio ${selectedBuilding.buildingid}`}`
              : `Resumen de ${buildings.length} edificio${buildings.length > 1 ? "s" : ""}`
            } · {new Date().toLocaleDateString("es-UY", { month: "long", year: "numeric" })}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Pill: Tu día (briefing IA) */}
          {(dailyBriefing?.markdown || loadingBriefing) && (
            <PillButton
              icon={<span style={{ fontSize: 14 }}>☀️</span>}
              label="Tu día"
              title="Tu día — briefing IA"
              onClick={() => setShowBriefing(true)}
              badge={dailyBriefing?.urgent_count > 0 ? (
                <span style={{ ...styles.pillBadge, background: colors.error, color: colors.white }}>
                  {dailyBriefing.urgent_count}
                </span>
              ) : null}
            />
          )}

          {/* Pill: Clima del edificio */}
          {selectedBuilding && sentiment?.latest && (
            <PillButton
              icon={(
                <span style={{ fontSize: 14 }}>
                  {sentiment.latest.score_label === "muy_positivo" ? "😄" :
                   sentiment.latest.score_label === "positivo" ? "🙂" :
                   sentiment.latest.score_label === "neutro" ? "😐" :
                   sentiment.latest.score_label === "tenso" ? "😟" : "😡"}
                </span>
              )}
              label="Clima"
              title="Clima del edificio"
              onClick={() => setShowSentiment(true)}
              badge={(
                <span style={{
                  ...styles.pillBadge,
                  background:
                    sentiment.latest.score >= 0.15 ? colors.successLight :
                    sentiment.latest.score > -0.15 ? colors.gray100 : colors.errorLight,
                  color:
                    sentiment.latest.score >= 0.15 ? colors.success :
                    sentiment.latest.score > -0.15 ? colors.gray700 : colors.error,
                }}>
                  {Number(sentiment.latest.score ?? 0).toFixed(2)}
                </span>
              )}
            />
          )}

          {/* Pill: Alertas IA */}
          {selectedBuilding && aiAlerts.length > 0 && (
            <PillButton
              icon={<FaExclamationTriangle size={12} color={colors.warning} />}
              label="Alertas"
              title="Alertas IA — gastos"
              onClick={() => setShowAlerts(true)}
              badge={(
                <span style={{ ...styles.pillBadge, background: colors.error, color: colors.white }}>
                  {aiAlerts.length}
                </span>
              )}
            />
          )}

          {selectedBuilding && (
            <button
              style={{
                ...styles.refreshBtn,
                background: colors.primary,
                color: colors.white,
                border: "none",
              }}
              onClick={openBenchmark}
              aria-label="Benchmark vs edificios similares"
            >
              📊 <span>Benchmark</span>
            </button>
          )}
          <button
            style={styles.refreshBtn}
            onClick={loadDashboardData}
          >
            <FaSync size={12} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* KPIs clickeables */}
      <div style={styles.kpiGrid} data-tutorial="home-kpis">
        <KPICard
          icon={FaMoneyBillWave}
          iconBg={colors.primaryLighter}
          iconColor={colors.primary}
          label={metrics.recaudacionLabel || "Recaudación del período"}
          value={formatCurrency(metrics.recaudacionTotal)}
          onClick={() => setSelected("cobranzaadmin")}
          badge={`${metrics.pctCumplimiento.toFixed(1)}% cumplimiento`}
          badgeColor={getCumplimientoBadgeColor(metrics.pctCumplimiento)}
        />

        {/* KPI 1a: Morosidad por Unidades */}
        <div style={{ position: "relative" }}>
          <KPICard
            icon={FaPercentage}
            iconBg={colors.warningLight}
            iconColor={colors.warning}
            label="Morosidad (Unidades)"
            value={`${metrics.morosidadUnidades.rate}%`}
            onClick={() => setSelected("cobranzaadmin")}
            badge={`${metrics.morosidadUnidades.unitsOverdue} de ${metrics.morosidadUnidades.totalUnits}`}
            badgeColor={getKpiBadgeColor("tasa_morosidad_unidades", metrics.morosidadUnidades.rate)}
          />
          <div style={styles.tooltipWrap}>
            <KpiInfoTooltip kpiKey="tasa_morosidad_unidades" />
          </div>
        </div>

        {/* KPI 1b: Morosidad por Monto */}
        <div style={{ position: "relative" }}>
          <KPICard
            icon={FaMoneyBillWave}
            iconBg={colors.errorLight}
            iconColor={colors.error}
            label="Morosidad (Monto)"
            value={`${metrics.morosidadMonto.rate}%`}
            onClick={() => setSelected("cobranzaadmin")}
            badge={formatCurrency(metrics.morosidadMonto.overdueAmount)}
            badgeColor={getKpiBadgeColor("tasa_morosidad_monto", metrics.morosidadMonto.rate)}
          />
          <div style={styles.tooltipWrap}>
            <KpiInfoTooltip kpiKey="tasa_morosidad_monto" />
          </div>
        </div>

        <KPICard
          icon={FaExclamationTriangle}
          iconBg={colors.errorLight}
          iconColor={colors.error}
          label="Incidencias Pendientes"
          value={metrics.incidenciasCriticas}
          onClick={() => setSelected("incidenciasedificio")}
          badge={`de ${metrics.incidenciasTotal} totales`}
          badgeColor="success"
        />

        {/* KPI 7 badge en Flujo de Caja */}
        <div style={{ position: "relative" }}>
          <KPICard
            icon={FaChartLine}
            iconBg={metrics.flujoCaja >= 0 ? colors.successLight : colors.errorLight}
            iconColor={metrics.flujoCaja >= 0 ? colors.success : colors.error}
            label="Flujo de Caja"
            value={formatCurrency(metrics.flujoCaja)}
            onClick={() => setSelected("estadocuentaedificio")}
            badge={`${metrics.mesesReserva.months} meses reserva`}
            badgeColor={getKpiBadgeColor("meses_reserva", metrics.mesesReserva.months)}
          />
          <div style={styles.tooltipWrap}>
            <KpiInfoTooltip kpiKey="meses_reserva" />
          </div>
        </div>

        {/* KPI 5: Ratio de Gastos Operativos - oculto temporalmente */}
      </div>

      {/* Acceso rápido: atajos a la gestión diaria. Las pantallas siguen en su
          sección del menú lateral — esto solo las acerca desde el inicio.
          Tiles estilo KPICard (cuadrado de ícono con fondo de estado + label). */}
      <div style={styles.quickAccess} data-tutorial="home-acciones">
        <div style={styles.quickAccessLabel}>
          <FaBolt size={11} color={colors.primary} />
          <span>Acceso rápido</span>
        </div>
        <div style={styles.quickAccessGrid}>
          {[
            { id: "pagosedificio", icon: FaCreditCard, label: "Gastos", bg: colors.warningLight, fg: colors.warning },
            { id: "cobranzaadmin", icon: FaMoneyBillWave, label: "Cobranza", bg: colors.primaryLighter, fg: colors.primary, badge: adminBadges.pendingVerification },
            { id: "incidenciasedificio", icon: FaExclamationTriangle, label: "Incidencias", bg: colors.errorLight, fg: colors.error, badge: adminBadges.activeIncidents },
            { id: "novedadesedificio", icon: FaNewspaper, label: "Enviar aviso", bg: colors.infoLight, fg: colors.info },
            { id: "informegastos", icon: FaReceipt, label: "Informe de gastos", bg: colors.gray100, fg: colors.gray600 },
            { id: "whatsappinbox", icon: FaComments, label: "Chats Habi", bg: colors.successLight, fg: colors.success, badge: adminBadges.whatsappNeedsHuman },
          ].map(({ id, icon: Icon, label, bg, fg, badge = 0 }) => (
            <button
              key={id}
              style={styles.quickAccessTile}
              className="habitia-card-hover"
              onClick={() => setSelected(id)}
            >
              <span style={{ ...styles.quickAccessIcon, background: bg }}>
                <Icon size={16} color={fg} />
              </span>
              <span style={styles.quickAccessText}>{label}</span>
              {badge > 0 && (
                <span
                  style={styles.quickAccessBadge}
                  aria-label={`${badge} ${badge === 1 ? "pendiente" : "pendientes"}`}
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {asambleasHoy.length > 0 && (
        <div style={styles.alertsSection}>
          <h2 style={styles.alertsSectionTitle}>Asambleas de hoy</h2>
          <div style={styles.alertsList}>
            {asambleasHoy.map((a) => {
              const sala = modoSalaDisponible(a, ahoraAsambleas);
              return (
                <div key={a.id} style={styles.alertItem}>
                  <div style={{ ...styles.alertDot, backgroundColor: colors.success }} />
                  <div style={styles.alertContent}>
                    <span style={styles.alertText}>
                      <span style={{ color: colors.black }}>{horaMontevideo(Number(a.inicioMs))}</span>: asamblea{" "}
                      {a.tipo === "extraordinaria" ? "extraordinaria" : "ordinaria"} · {a.modalidad === "mixta" ? "mixta" : "presencial"}
                      {a.lugar ? ` en ${a.lugar}` : ""}
                      {!sala.habilitado && sala.desdeMs ? ` · el Modo Sala se habilita a las ${horaMontevideo(sala.desdeMs)}` : ""}
                    </span>
                    <span style={{ display: "inline-flex", gap: spacing[2], flexWrap: "wrap" }}>
                      <button style={styles.alertAction} onClick={() => setSelected("asambleas")}>
                        Ver asamblea
                      </button>
                      <button
                        style={sala.habilitado ? styles.alertActionPrincipal : { ...styles.alertActionPrincipal, ...styles.alertActionApagada }}
                        onClick={() => abrirModoSala(a.id)}
                        disabled={!sala.habilitado}
                      >
                        Abrir Modo Sala
                      </button>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {asambleasSiguientes.length > 0 && (
        <div style={styles.alertsSection}>
          <h2 style={styles.alertsSectionTitle}>Asambleas próximas</h2>
          <div style={styles.alertsList}>
            {asambleasSiguientes.map((a) => {
              const r = a.resumen || {};
              const cuando = new Date(Number(a.inicioMs)).toLocaleString("es-UY", {
                timeZone: "America/Montevideo", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
              });
              return (
                <div key={a.id} style={styles.alertItem}>
                  <div style={{ ...styles.alertDot, backgroundColor: colors.primary }} />
                  <div style={styles.alertContent}>
                    <span style={styles.alertText}>
                      <strong>{cuando}</strong>: asamblea {a.tipo === "extraordinaria" ? "extraordinaria" : "ordinaria"}
                      {typeof r.total === "number" && r.total > 0
                        ? ` · ${r.confirmados || 0} confirmados, ${r.sinRespuesta || 0} sin respuesta`
                        : ""}
                    </span>
                    <button style={styles.alertAction} onClick={() => setSelected("asambleas")}>
                      Ver asamblea
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Alertas Actionables */}
      {buildingsHealth.some(b => b.estado !== "green") && (
        <div style={styles.alertsSection}>
          <h2 style={styles.alertsSectionTitle}>Alertas que requieren atencion</h2>
          <div style={styles.alertsList}>
            {buildingsHealth.filter(b => b.morosidad > 15).map((b, i) => (
              <div key={`mor-${b.id}-${i}`} style={styles.alertItem}>
                <div style={{...styles.alertDot, backgroundColor: colors.warning}} />
                <div style={styles.alertContent}>
                  <span style={styles.alertText}>
                    <strong>{b.name}</strong>: morosidad al {b.morosidad.toFixed(0)}%
                  </span>
                  <button
                    style={styles.alertAction}
                    onClick={() => goToBuildingScreen(b, "cobranzaadmin")}
                  >
                    Ver detalles
                  </button>
                </div>
              </div>
            ))}
            {buildingsHealth.filter(b => b.incidenciasPendientes > 3).map((b, i) => (
              <div key={`inc-${b.id}-${i}`} style={styles.alertItem}>
                <div style={{...styles.alertDot, backgroundColor: colors.error}} />
                <div style={styles.alertContent}>
                  <span style={styles.alertText}>
                    <strong>{b.name}</strong>: {b.incidenciasPendientes} incidencias sin resolver
                  </span>
                  <button
                    style={styles.alertAction}
                    onClick={() => goToBuildingScreen(b, "incidenciasedificio")}
                  >
                    Ver incidencias
                  </button>
                </div>
              </div>
            ))}
            {buildingsHealth.filter(b => !b.cierreMes).map((b, i) => (
              <div key={`cierre-${b.id}-${i}`} style={styles.alertItem}>
                <div style={{...styles.alertDot, backgroundColor: colors.gray600}} />
                <div style={styles.alertContent}>
                  <span style={styles.alertText}>
                    <strong>{b.name}</strong>: cierre de mes pendiente
                  </span>
                  <button
                    style={styles.alertAction}
                    onClick={() => goToBuildingScreen(b, "cierredemes")}
                  >
                    Ir a cierre
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabla de Semáforo Operativo */}
      <div style={styles.tableSection} data-tutorial="home-semaforo">
        <div style={styles.tableSectionHeader}>
          <h2 style={styles.tableTitle}>Semáforo Operativo</h2>
          <div style={styles.tableBadges}>
            <StatusBadge status="critico" label={`${buildingsHealth.filter(b => b.estado === "red").length} Críticos`} size="sm" />
            <StatusBadge status="atencion" label={`${buildingsHealth.filter(b => b.estado === "yellow").length} Atención`} size="sm" />
            <StatusBadge status="normal" label={`${buildingsHealth.filter(b => b.estado === "green").length} Normal`} size="sm" />
          </div>
        </div>

        <DataTable
          columns={[
            {
              key: "name",
              label: "Edificio",
              render: (_v, b) => (
                <div style={styles.buildingCell}>
                  <div style={styles.buildingIcon}>
                    <FaBuilding size={12} color={colors.primary} />
                  </div>
                  <div>
                    <div style={styles.buildingName}>{b.name}</div>
                    <div style={styles.buildingUnits}>{b.unidades} unidades</div>
                  </div>
                </div>
              ),
            },
            {
              key: "estado",
              label: "Estado",
              align: "center",
              render: (estado) => {
                const key = estado === "red" ? "critico" : estado === "yellow" ? "atencion" : "normal";
                return <StatusBadge status={key} variant="dot" size="sm" />;
              },
            },
            {
              key: "morosidad",
              label: "Morosidad",
              align: "right",
              render: (v) => (
                <span style={{
                  color: v > 20 ? colors.error : v > 10 ? colors.warning : colors.gray900,
                  fontWeight: 400,
                }}>
                  {v.toFixed(1)}%
                </span>
              ),
            },
            {
              key: "incidenciasPendientes",
              label: "Alertas",
              align: "center",
              render: (v) => (
                <span style={{
                  ...styles.alertBadge,
                  backgroundColor: v > 0 ? colors.errorLight : colors.gray100,
                  color: v > 0 ? colors.errorDark : colors.gray600,
                }}>
                  {v}
                </span>
              ),
            },
            {
              key: "cierreMes",
              label: "Cierre Mes",
              align: "center",
              render: (v) => (
                <StatusBadge
                  status={v ? "completada" : "pendiente"}
                  label={v ? "Completo" : "Pendiente"}
                  size="sm"
                />
              ),
            },
          ]}
          data={buildingsHealth}
          emptyIcon={FaBuilding}
          emptyTitle="Sin edificios"
          emptyDescription="No hay edificios para mostrar."
        />
      </div>

      {/* F3 — Modal de Benchmark vs edificios similares */}
      <AdminModal
        open={showBenchmark}
        onClose={() => setShowBenchmark(false)}
        title="Benchmark vs edificios similares"
        size="wide"
      >
        {loadingBenchmark && (
          <LoadingState variant="block" message="Comparando con edificios similares…" />
        )}

        {!loadingBenchmark && benchmark && (
              <>
                <p style={{ fontSize: 13, color: colors.gray600, marginTop: 0 }}>
                  {benchmark.building_name} ({benchmark.target_units} unidades) vs {benchmark.peer_count} edificios similares en {benchmark.department || "—"} · últimos {benchmark.months_window} meses · gasto por unidad/mes.
                </p>

                {benchmark.error && (
                  <div style={{ padding: 12, background: colors.warningLight, color: colors.warning, borderRadius: 8 }}>
                    {benchmark.error}
                  </div>
                )}

                {benchmark.narratives?.length > 0 && (
                  <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 400, color: colors.primary }}>
                      Oportunidades de optimización
                    </h3>
                    {benchmark.narratives.map((n, i) => (
                      <div key={i} style={{ padding: 12, background: colors.primaryLighter, borderRadius: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 9999, background: colors.primary, color: colors.white, textTransform: "uppercase", letterSpacing: 0.4 }}>
                            {n.category}
                          </span>
                          <strong style={{ fontSize: 13, fontWeight: 400, color: colors.gray900 }}>
                            {n.headline}
                          </strong>
                        </div>
                        <div style={{ fontSize: 13, color: colors.gray700, lineHeight: 1.5, marginBottom: 4 }}>
                          {n.narrative}
                        </div>
                        {n.suggested_action && (
                          <div style={{ fontSize: 12, color: colors.gray900, fontStyle: "italic" }}>
                            → {n.suggested_action}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <h3 style={{ marginTop: 20, marginBottom: 8, fontSize: 16, fontWeight: 400, color: colors.gray900 }}>
                  Detalle por categoría
                </h3>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: colors.gray50 }}>
                      <th style={{ padding: 8, textAlign: "left", fontSize: 12, fontWeight: 400, color: colors.gray700 }}>Categoría</th>
                      <th style={{ padding: 8, textAlign: "right", fontSize: 12, fontWeight: 400, color: colors.gray700 }}>Tu edificio</th>
                      <th style={{ padding: 8, textAlign: "right", fontSize: 12, fontWeight: 400, color: colors.gray700 }}>Mediana flota</th>
                      <th style={{ padding: 8, textAlign: "right", fontSize: 12, fontWeight: 400, color: colors.gray700 }}>Percentil</th>
                      <th style={{ padding: 8, textAlign: "right", fontSize: 12, fontWeight: 400, color: colors.gray700 }}>Comparables</th>
                    </tr>
                  </thead>
                  <tbody>
                    {benchmark.items.map((item) => {
                      const pct = item.percentile;
                      const pctColor =
                        pct === null ? colors.gray400 :
                        pct >= 75 ? colors.error :
                        pct >= 50 ? colors.warning : colors.success;
                      return (
                        <tr key={item.category} style={{ borderTop: `1px solid ${colors.gray100}` }}>
                          <td style={{ padding: 8, fontSize: 13, color: colors.gray900, textTransform: "capitalize" }}>{item.category}</td>
                          <td style={{ padding: 8, fontSize: 13, color: colors.gray900, textAlign: "right" }}>
                            {item.target_per_unit_month > 0 ? `$ ${item.target_per_unit_month.toLocaleString("es-UY")}` : "—"}
                          </td>
                          <td style={{ padding: 8, fontSize: 13, color: colors.gray700, textAlign: "right" }}>
                            {item.peer_median != null ? `$ ${item.peer_median.toLocaleString("es-UY")}` : "—"}
                          </td>
                          <td style={{ padding: 8, textAlign: "right" }}>
                            <span style={{ color: pctColor, fontWeight: 400, fontSize: 13 }}>
                              {pct == null ? "—" : `P${pct.toFixed(0)}`}
                            </span>
                          </td>
                          <td style={{ padding: 8, fontSize: 12, color: colors.gray600, textAlign: "right" }}>
                            {item.peer_count}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
      </AdminModal>

      {/* D6 — Modal Briefing del día */}
      {showBriefing && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 1000, display: "flex", alignItems: "center",
            justifyContent: "center", padding: 20,
          }}
          onClick={() => setShowBriefing(false)}
        >
          <div
            style={{
              background: colors.white, borderRadius: 12, maxWidth: 760, width: "100%",
              maxHeight: "85vh", overflowY: "auto", padding: 24,
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 400, color: colors.primary }}>
                ☀️ Tu día — briefing IA
              </h2>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={regenerateBriefing}
                  disabled={loadingBriefing}
                  style={{
                    padding: "6px 12px", fontSize: 12, color: colors.primary,
                    background: "transparent", border: `1px solid ${colors.primary}`,
                    borderRadius: 8, cursor: loadingBriefing ? "not-allowed" : "pointer",
                  }}
                >
                  {loadingBriefing ? "Generando…" : "Regenerar"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBriefing(false)}
                  style={{ background: "transparent", border: "none", fontSize: 24, color: colors.gray600, cursor: "pointer" }}
                  aria-label="Cerrar"
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{
              padding: 16, background: colors.primaryLighter, borderRadius: 8,
              fontSize: 14, lineHeight: 1.55, color: colors.gray900, whiteSpace: "pre-wrap",
            }}>
              {dailyBriefing?.markdown || "Generando…"}
            </div>
          </div>
        </div>
      )}

      {/* D4 — Modal Clima del edificio */}
      {showSentiment && sentiment?.latest && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 1000, display: "flex", alignItems: "center",
            justifyContent: "center", padding: 20,
          }}
          onClick={() => setShowSentiment(false)}
        >
          <div
            style={{
              background: colors.white, borderRadius: 12, maxWidth: 560, width: "100%",
              maxHeight: "85vh", overflowY: "auto", padding: 24,
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 400, color: colors.gray900 }}>
                Clima del edificio
              </h2>
              <button
                type="button"
                onClick={() => setShowSentiment(false)}
                style={{ background: "transparent", border: "none", fontSize: 24, color: colors.gray600, cursor: "pointer" }}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <span style={{ fontSize: 36 }}>
                {sentiment.latest.score_label === "muy_positivo" ? "😄" :
                 sentiment.latest.score_label === "positivo" ? "🙂" :
                 sentiment.latest.score_label === "neutro" ? "😐" :
                 sentiment.latest.score_label === "tenso" ? "😟" : "😡"}
              </span>
              <div>
                <div style={{ fontSize: 22, fontWeight: 400, color: colors.gray900, textTransform: "capitalize" }}>
                  {(sentiment.latest.score_label || "neutro").replace("_", " ")}
                </div>
                <div style={{ fontSize: 13, color: colors.gray600, marginTop: 2 }}>
                  Score {Number(sentiment.latest.score ?? 0).toFixed(2)} · {sentiment.latest.sample_count} comunicaciones
                </div>
              </div>
            </div>

            {sentiment.latest.top_themes?.length > 0 && (
              <div style={{ fontSize: 13, color: colors.gray700, marginBottom: 10 }}>
                <strong style={{ fontWeight: 400, color: colors.gray900 }}>Temas:</strong> {sentiment.latest.top_themes.join(" · ")}
              </div>
            )}
            {sentiment.history?.length > 1 && (
              <div style={{ fontSize: 12, color: colors.gray500 }}>
                Tendencia últimas {sentiment.history.length} semanas: {
                  sentiment.history.slice().reverse().map((h) => h.score?.toFixed(2)).join(" → ")
                }
              </div>
            )}
          </div>
        </div>
      )}

      {/* A3 — Modal Alertas IA */}
      {showAlerts && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 1000, display: "flex", alignItems: "center",
            justifyContent: "center", padding: 20,
          }}
          onClick={() => setShowAlerts(false)}
        >
          <div
            style={{
              background: colors.white, borderRadius: 12, maxWidth: 760, width: "100%",
              maxHeight: "85vh", overflowY: "auto", padding: 24,
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FaExclamationTriangle size={18} color={colors.warning} />
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 400, color: colors.gray900 }}>
                  Alertas IA — gastos
                </h2>
                <span style={{
                  padding: "2px 10px", fontSize: 11, borderRadius: 9999,
                  background: colors.warningLight, color: colors.warningDark || colors.warning,
                }}>
                  {aiAlerts.length} sin revisar
                </span>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={fetchAIAlerts}
                  disabled={loadingAlerts}
                  style={{
                    padding: "6px 12px", fontSize: 12, color: colors.gray700,
                    background: "transparent", border: `1px solid ${colors.gray300}`,
                    borderRadius: 8, cursor: loadingAlerts ? "not-allowed" : "pointer",
                  }}
                >
                  {loadingAlerts ? "Cargando…" : "Actualizar"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAlerts(false)}
                  style={{ background: "transparent", border: "none", fontSize: 24, color: colors.gray600, cursor: "pointer" }}
                  aria-label="Cerrar"
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {aiAlerts.map((alert) => {
                const sevColor = alert.severity === "alta" ? colors.error : colors.warning;
                const sevBg = alert.severity === "alta" ? colors.errorLight : colors.warningLight;
                return (
                  <div key={alert.id} style={{
                    padding: 12, borderRadius: 8, background: sevBg,
                    display: "flex", flexDirection: "column", gap: 6,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{
                            padding: "2px 10px", fontSize: 11, borderRadius: 9999,
                            background: colors.white, color: sevColor,
                            textTransform: "uppercase", letterSpacing: 0.4,
                          }}>
                            {alert.severity}
                          </span>
                          <strong style={{ fontSize: typography.sizes.sm, color: colors.gray900, fontWeight: 400 }}>
                            {alert.title}
                          </strong>
                        </div>
                        <div style={{ fontSize: 13, color: colors.gray700, lineHeight: 1.45 }}>
                          {alert.narrative}
                        </div>
                        {alert.suggested_action && (
                          <div style={{ fontSize: 12, color: colors.gray900, fontStyle: "italic" }}>
                            → {alert.suggested_action}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <button
                          type="button"
                          onClick={() => updateAlertStatus(alert.id, "reviewed")}
                          style={{
                            padding: "4px 10px", fontSize: 11, color: colors.primary,
                            background: colors.white, border: "none", borderRadius: 8, cursor: "pointer",
                          }}
                        >
                          Revisar
                        </button>
                        <button
                          type="button"
                          onClick={() => updateAlertStatus(alert.id, "dismissed")}
                          style={{
                            padding: "4px 10px", fontSize: 11, color: colors.gray600,
                            background: "transparent", border: "none", borderRadius: 8, cursor: "pointer",
                          }}
                        >
                          Descartar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {aiAlerts.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: colors.gray600, fontSize: 13 }}>
                  No hay alertas sin revisar.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// ─── Estilos con design tokens ──────────────────────────────────────────────
const styles = {
  container: {
    ...pageContainer,
    minHeight: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[6],
  },
  title: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.primary,
    margin: 0,
    display: "flex",
    alignItems: "center",
  },
  subtitle: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: "4px 0 0 0",
    textTransform: "capitalize",
  },
  refreshBtn: {
    ...btnSecondary,
    padding: "8px 14px",
    fontSize: typography.sizes.base,
  },
  pillBadge: {
    padding: "1px 8px",
    fontSize: 11,
    borderRadius: 9999,
    lineHeight: 1.5,
    fontWeight: 400,
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: spacing[4],
    marginBottom: spacing[6],
  },
  tooltipWrap: {
    position: "absolute",
    top: spacing[2],
    right: spacing[2],
  },
  // Acceso rápido — tiles con el patrón de KPICard (ícono en cuadrado de color
  // de estado + label), jerarquía por color/uppercase según DESIGN_SYSTEM.
  quickAccess: {
    marginBottom: spacing[6],
  },
  quickAccessLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: typography.sizes.xs,
    color: colors.gray600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: spacing[3],
  },
  quickAccessGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(158px, 1fr))",
    gap: spacing[3],
  },
  quickAccessTile: {
    ...cardBase,
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    padding: `${spacing[3]}px ${spacing[4]}px`,
    border: `1px solid ${colors.gray200}`,
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "left",
  },
  // Contador de pendientes sobre la esquina del tile (mismo rojo y formato
  // "99+" que los badges del sidebar; borde blanco para despegarlo del card).
  quickAccessBadge: {
    position: "absolute",
    top: -7,
    right: -7,
    backgroundColor: colors.error,
    color: colors.white,
    fontSize: 11,
    fontWeight: 400,
    borderRadius: 999,
    padding: "1px 6px",
    minWidth: 16,
    textAlign: "center",
    lineHeight: "16px",
    border: `2px solid ${colors.white}`,
  },
  quickAccessIcon: {
    width: 38,
    height: 38,
    borderRadius: radius.md,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  quickAccessText: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray900,
    whiteSpace: "nowrap",
  },
  // Sección de alertas
  alertsSection: {
    ...cardBase,
    marginBottom: spacing[6],
  },
  alertsSectionTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    margin: `0 0 ${spacing[4]}px 0`,
  },
  alertsList: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
  },
  alertItem: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    padding: `${spacing[3]}px ${spacing[4]}px`,
    backgroundColor: colors.gray50,
    borderRadius: radius.sm,
  },
  alertDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },
  alertContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flex: 1,
    gap: spacing[3],
    flexWrap: "wrap",
  },
  alertText: {
    fontSize: typography.sizes.base,
    color: colors.gray900,
  },
  alertAction: {
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    color: colors.primary,
    backgroundColor: "transparent",
    border: `1px solid ${colors.primary}`,
    borderRadius: radius.sm - 2,
    padding: "4px 12px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: typography.fontFamily,
    transition: `all ${transitions.fast}`,
  },
  // "Abrir Modo Sala" en Asambleas de hoy: la acción principal del bloque.
  alertActionPrincipal: {
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    color: colors.white,
    backgroundColor: colors.primaryDark,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.primaryDark,
    borderRadius: radius.sm,
    padding: "4px 12px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: typography.fontFamily,
    transition: `all ${transitions.fast}`,
  },
  alertActionApagada: {
    backgroundColor: colors.gray100,
    borderColor: colors.gray200,
    color: colors.gray600,
    cursor: "default",
  },
  // Sección de tabla
  tableSection: {
    ...cardBase,
    padding: 0,
    overflow: "hidden",
  },
  tableSectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${spacing[4]}px ${spacing[5]}px`,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  tableTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    margin: 0,
  },
  tableBadges: {
    display: "flex",
    gap: spacing[2],
  },
  buildingCell: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
  },
  buildingIcon: {
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    backgroundColor: colors.primaryLighter,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  buildingName: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.black,
  },
  buildingUnits: {
    fontSize: typography.sizes.xs,
    color: colors.gray400,
  },
  alertBadge: {
    display: "inline-block",
    minWidth: 24,
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    textAlign: "center",
  },
};

export default HomeAdmin;
